<template>
  <div class="h-full">
    <!-- Overlay (solo para drawer) -->
    <slot name="overlay" />

    <!-- Container principal -->
    <div :class="containerClasses" :style="containerStyle">
      <!-- Header -->
      <div
        class="p-5 border-b border-gray-100 flex items-center justify-between bg-white shrink-0"
      >
        <div class="flex items-center gap-3">
          <h2 class="text-xl font-serif font-bold flex items-center gap-2">
            <ShoppingBag :size="20" /> Your Order
          </h2>
        </div>
        <div class="flex items-center gap-2">
          <!-- Pin/Unpin Button -->
          <Button
            v-if="showPinButton"
            @click.stop.prevent="handlePinAction"
            :icon="pinButtonType === 'pin' ? Pin : PinOff"
            :icon-size="20"
            variant="ghost"
            :custom-class="
              pinButtonType === 'pin'
                ? 'hidden lg:flex p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500'
                : 'p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500'
            "
            circular
            size="sm"
          />
          <!-- Close Button -->
          <Button
            v-if="showCloseButton"
            @click="onClose"
            :custom-class="closeButtonClass"
            :icon="X"
            :icon-size="24"
            size="sm"
            variant="ghost"
            circular
          />

          <!-- Custom header actions slot -->
          <slot name="header-actions" />
        </div>
      </div>

      <!-- Content Area -->
      <div class="flex-1 overflow-y-auto bg-white">
        <!-- Empty State -->
        <div
          v-if="items.length === 0"
          class="h-full flex flex-col items-center justify-center text-gray-400 gap-4 p-8"
        >
          <div class="bg-primary-50 p-6 rounded-full">
            <ShoppingBag :size="48" class="opacity-50 text-primary-600" />
          </div>
          <p class="font-medium text-lg">Your cart is empty</p>
          <slot name="empty-action">
            <Button
              @click="onClose"
              label="Start Ordering"
              variant="ghost"
              custom-class="mt-4 bg-white border border-gray-200 px-6 py-2 rounded-full text-gray-900 font-bold hover:bg-gray-50 transition-colors"
              size="md"
            />
          </slot>
        </div>

        <!-- Items List -->
        <div v-else class="p-5 space-y-6">
          <div
            v-for="item in items"
            :key="item.cartId"
            class="flex gap-4 fadeIn-0-3s"
          >
            <!-- Item Image -->
            <div
              class="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 border border-gray-100 mt-1 bg-gray-100"
            >
              <img
                v-if="getItemImage(item)"
                :src="getItemImage(item)"
                class="w-full h-full object-contain"
                :alt="item.name"
                @error="handleImageError"
              />
              <div
                v-else
                class="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200"
              >
                <FileImage :size="20" class="text-gray-400" />
              </div>
            </div>

            <!-- Item Content -->
            <div class="flex-1">
              <div class="flex justify-between items-start mb-1">
                <h4 class="font-semibold text-gray-900 line-clamp-1 text-base">
                  {{ item.name }}
                </h4>
                <div class="flex flex-col items-end gap-1">
                  <div
                    v-if="item.quantity > 1"
                    class="flex items-baseline justify-end gap-2 text-sm whitespace-nowrap"
                  >
                    <span
                      v-if="hasDiscount(item)"
                      class="text-gray-400 line-through"
                      >{{ formatPrice(getUnitPrice(item)) }}</span
                    >
                    <span
                      :class="
                        hasDiscount(item)
                          ? 'text-green-700 font-semibold'
                          : 'text-gray-400'
                      "
                      >{{ formatPrice(getDiscountedUnitPrice(item)) }} each</span
                    >
                  </div>
                  <div
                    class="flex items-baseline justify-end gap-2.5 whitespace-nowrap"
                  >
                    <span
                      v-if="hasDiscount(item)"
                      class="text-base text-gray-400 line-through"
                      >{{
                        formatPrice(getItemTotalWithoutDiscount(item))
                      }}</span
                    >
                    <span class="font-bold text-lg text-primary-600">
                      {{ formatPrice(getItemTotal(item)) }}
                    </span>
                  </div>
                </div>
              </div>

              <div
                v-if="
                  item.selectedModifiers && item.selectedModifiers.length > 0
                "
                class="mb-1 space-y-0.5"
              >
                <p
                  v-for="(modifier, index) in item.selectedModifiers"
                  :key="index"
                  class="text-xs text-gray-500 leading-relaxed"
                >
                  {{ modifier.name }}
                </p>
              </div>

              <p v-if="item.note" class="text-xs text-gray-500 mb-2 italic">
                "{{ item.note }}"
              </p>

              <div
                v-if="getPromotionLabels(item).length"
                class="mb-2 flex flex-wrap gap-1"
              >
                <span
                  v-for="label in getPromotionLabels(item)"
                  :key="`${item.cartId}-${label}`"
                  class="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-semibold text-green-700"
                >
                  <Tag :size="11" />
                  {{ label }}
                </span>
              </div>

              <!-- Special warning for items with alert message -->
              <Alert
                v-if="item.alertMessage"
                variant="warning"
                :message="item.alertMessage"
                :icon="AlertCircle"
                :dismissible="false"
                custom-class="mt-1.5 mb-2"
              />

              <!-- Quantity Controls -->
              <div class="flex items-center justify-between mt-3">
                <div class="flex items-center bg-gray-100 rounded-lg shadow-sm">
                  <Button
                    @click="onUpdateQuantity(item.cartId, -1)"
                    :disabled="item.quantity <= 1"
                    variant="ghost"
                    :custom-class="
                      useQuantityDropdown
                        ? 'w-12 h-12 md:w-10 md:h-10 flex items-center justify-center text-gray-600 hover:bg-gray-200 hover:text-primary-600 rounded-l-lg disabled:opacity-30 transition-colors'
                        : 'w-9 h-9 flex items-center justify-center text-gray-600 hover:bg-gray-200 hover:text-primary-600 rounded-l-lg disabled:opacity-30 transition-colors'
                    "
                    size="sm"
                  >
                    <Minus :size="16" style="stroke-width: 3;" />
                  </Button>
                  <select
                    v-if="useQuantityDropdown"
                    :value="item.quantity"
                    @change="handleQuantitySelect(item, $event)"
                    aria-label="Select quantity"
                    class="h-12 min-w-[5rem] border-x border-gray-200 bg-white px-3 text-center text-lg font-bold text-gray-900 outline-none transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-500 md:h-10 md:min-w-[4.5rem] md:text-base"
                  >
                    <option
                      v-for="quantityOption in getQuantityOptions(item)"
                      :key="`${item.cartId}-${quantityOption}`"
                      :value="quantityOption"
                    >
                      {{ quantityOption }}
                    </option>
                  </select>
                  <span
                    v-if="!useQuantityDropdown"
                    class="w-10 text-center text-base font-bold text-gray-900"
                    >{{ item.quantity }}</span
                  >
                  <Button
                    @click="onUpdateQuantity(item.cartId, 1)"
                    variant="ghost"
                    :custom-class="
                      useQuantityDropdown
                        ? 'w-12 h-12 md:w-10 md:h-10 flex items-center justify-center text-gray-600 hover:bg-gray-200 hover:text-primary-600 rounded-r-lg transition-colors'
                        : 'w-9 h-9 flex items-center justify-center text-gray-600 hover:bg-gray-200 hover:text-primary-600 rounded-r-lg transition-colors'
                    "
                    size="sm"
                  >
                    <Plus :size="16" style="stroke-width: 3;" />
                  </Button>
                </div>
                <div class="flex items-center gap-1">
                  <Button
                    v-if="onEditItem"
                    @click="onEditItem(item)"
                    variant="ghost"
                    custom-class="flex items-center gap-1.5 px-2.5 py-1.5 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors text-sm font-medium"
                    size="sm"
                    title="Edit item"
                  >
                    <Pencil :size="16" class="text-primary-600" />
                    <span>Edit</span>
                  </Button>
                  <Button
                    @click="onRemoveItem(item.cartId)"
                    variant="ghost"
                    custom-class="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                    circular
                    size="sm"
                  >
                    <Trash2 :size="20" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div
        v-if="items.length > 0"
        class="p-6 bg-white border-t border-gray-100 z-10"
        style="box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.05);"
      >
        <div class="space-y-2 mb-4 text-sm">
          <div class="flex justify-between text-gray-500">
            <span>Subtotal</span>
            <span>{{ formatPrice(grossSubtotal) }}</span>
          </div>
          <template v-if="appliedPromotions.length">
            <div
              v-for="promotion in appliedPromotions"
              :key="`${promotion.id}-${promotion.type}`"
              class="flex justify-between gap-3 text-green-700 font-semibold"
            >
              <span class="flex items-center gap-1.5 min-w-0">
                <Tag :size="14" class="flex-shrink-0" />
                <span class="truncate">{{ promotion.name }}</span>
              </span>
              <span class="flex-shrink-0">
                &minus;{{ formatPrice(promotion.discount) }}
              </span>
            </div>
          </template>
          <div
            v-else-if="discountTotal > 0"
            class="flex justify-between text-green-700 font-semibold"
          >
            <span class="flex items-center gap-1.5">
              <Tag :size="14" />
              Discount
            </span>
            <span>&minus;{{ formatPrice(discountTotal) }}</span>
          </div>
          <div class="flex justify-between text-gray-500">
            <span>Tax</span>
            <span>{{ formatPrice(totalTax) }}</span>
          </div>
          <div
            v-if="grandTotal < minimumOrderRequired && minimumOrderRequired"
            class="flex justify-between text-red-500 text-xs pt-2"
          >
            <span>Min order allowed</span>
            <span>{{ formatPrice(minimumOrderRequired) }}</span>
          </div>
          <div
            class="flex justify-between font-bold text-xl text-gray-900 pt-3 border-t border-gray-100"
          >
            <span>Total</span>
            <span>{{ formatPrice(grandTotal) }}</span>
          </div>
        </div>

        <!-- Checkout / Back Buttons -->
        <div v-if="!isCheckoutActive" class="flex items-center gap-3">
          <!-- Back Button (continue ordering) -->
          <Button
            v-if="onClose"
            @click="handleBack"
            :icon="ArrowLeft"
            :icon-size="20"
            icon-position="left"
            variant="outline"
            custom-class="font-bold py-4 rounded-xl transition-all flex justify-center px-6 items-center gap-2 checkout-button"
            size="lg"
          >
            Back
          </Button>

          <Button
            @click="handleCheckout"
            :disabled="isMinimumOrderNotMet || isStoreClosed"
            :icon="ArrowRight"
            :icon-size="20"
            icon-position="right"
            variant="primary"
            custom-class="flex-1 font-bold py-4 rounded-xl shadow-lg hover:bg-primary-700 transition-all flex justify-between px-6 items-center checkout-button disabled:opacity-50 disabled:cursor-not-allowed"
            size="lg"
          >
            <span v-if="isStoreClosed">Store is Closed</span>
            <span v-else>Checkout</span>
          </Button>
        </div>

        <div
          v-if="isCheckoutActive"
          class="w-full bg-gray-100 text-gray-400 font-bold py-4 rounded-xl flex justify-center items-center text-sm"
        >
          Checkout in progress
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Button, Alert } from "@/components/ui";
import {
  getQuantitySelectOptions,
  isPoloBistroUrl
} from "@/utils/customStoreRules";
import {
  Pin,
  PinOff,
  ArrowRight,
  ArrowLeft,
  X,
  ShoppingBag,
  FileImage,
  AlertCircle,
  Plus,
  Minus,
  Trash2,
  Pencil,
  Tag
} from "lucide-vue";
import { calculateGrandTotal } from "@/helpers/orderHelpers";

export default {
  name: "CartBase",
  components: {
    Button,
    Alert,
    Tag
  },
  props: {
    items: {
      type: Array,
      default: () => []
    },
    orderConfig: {
      type: Object,
      required: true
    },
    // Solo props esenciales para funcionalidad
    containerClasses: {
      type: [String, Array],
      required: true
    },
    containerStyle: {
      type: String,
      default: ""
    },
    showPinButton: {
      type: Boolean,
      default: false
    },
    pinButtonType: {
      type: String,
      default: "pin",
      validator: (value) => ["pin", "unpin"].includes(value)
    },
    showCloseButton: {
      type: Boolean,
      default: false
    },
    isCheckoutActive: {
      type: Boolean,
      default: false
    },
    isStoreClosed: {
      type: Boolean,
      default: false
    },
    onUpdateQuantity: {
      type: Function,
      required: true
    },
    onRemoveItem: {
      type: Function,
      required: true
    },
    onEditItem: {
      type: Function,
      default: null
    },
    onCheckout: {
      type: Function,
      required: true
    },
    onPin: {
      type: Function,
      default: null
    },
    onUnpin: {
      type: Function,
      default: null
    },
    onClose: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      Pin,
      PinOff,
      ArrowRight,
      ArrowLeft,
      X,
      ShoppingBag,
      FileImage,
      AlertCircle,
      Plus,
      Minus,
      Trash2,
      Pencil
    };
  },
  computed: {
    // Usar valores del store
    subtotal() {
      return this.$store.getters.subtotal || 0;
    },
    totalTax() {
      return this.$store.getters.getTotalTax || 0;
    },
    discountTotal() {
      return this.$store.getters.getDiscountTotal || 0;
    },
    grossSubtotal() {
      // The stored subtotal is already discount-inclusive, so add the discount
      // back to show the pre-discount subtotal alongside the discount line.
      return this.subtotal + this.discountTotal;
    },
    appliedPromotions() {
      return this.$store.getters.getAppliedPromotions || [];
    },
    diningOptionObject() {
      return this.$store.getters.getDiningOptionObject;
    },
    isStoreTaxInclusive() {
      const store = this.$store.getters.getStore;
      return (
        store?.tax_option === "inclusive" ||
        this.$store.getters.isStoreTaxInclusive === true
      );
    },
    grandTotal() {
      // Total = Subtotal + Tax (sin Tip)
      // Use helper for consistent calculation
      return calculateGrandTotal(
        this.subtotal,
        this.totalTax,
        this.isStoreTaxInclusive
      );
    },
    minimumOrderRequired() {
      return this.diningOptionObject?.minimum_order_requirements || 0;
    },
    isMinimumOrderNotMet() {
      return (
        this.minimumOrderRequired > 0 &&
        this.grandTotal < this.minimumOrderRequired
      );
    },
    currency() {
      return this.$store.getters.getCurrencySymbol || "$";
    },
    formatPrice() {
      return this.$store.getters.formatPrice;
    },
    useQuantityDropdown() {
      return isPoloBistroUrl(this.$route?.path);
    },
    closeButtonClass() {
      return "p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500";
    }
  },
  methods: {
    getItemImage(item) {
      return item?.image || item?.image_url || null;
    },
    handleImageError(event) {
      event.target.style.display = "none";
    },
    hasDiscount(item) {
      const discount = item.discount || 0;
      const wholeDiscount = item.wholeDiscount || 0;
      return discount + wholeDiscount > 0;
    },
    getUnitPrice(item) {
      const modifiersPrice =
        item.selectedModifiers?.reduce((sum, m) => sum + (m.price || 0), 0) ||
        0;
      return Number(item.price || 0) + modifiersPrice;
    },
    getDiscountedUnitPrice(item) {
      const quantity = Number(item.quantity || 0);
      if (quantity <= 0) return this.getUnitPrice(item);
      return this.getItemTotal(item) / quantity;
    },
    getItemTotalWithoutDiscount(item) {
      return item.itemTotalWithoutDiscount !== undefined
        ? item.itemTotalWithoutDiscount
        : (item.price +
            (item.selectedModifiers?.reduce((sum, m) => sum + m.price, 0) ||
              0)) *
            item.quantity;
    },
    getItemTotal(item) {
      return item.itemTotal !== undefined
        ? item.itemTotal
        : (item.price +
            (item.selectedModifiers?.reduce((sum, m) => sum + m.price, 0) ||
              0)) *
            item.quantity -
            (item.discount || 0) -
            (item.wholeDiscount || 0);
    },
    getPromotionLabels(item) {
      if (!Array.isArray(item.promotionLabels)) return [];
      return [...new Set(item.promotionLabels.filter(Boolean))];
    },
    getQuantityOptions(item) {
      return getQuantitySelectOptions(item?.quantity);
    },
    handleQuantitySelect(item, event) {
      const selectedQuantity = Number(event.target.value);
      const currentQuantity = Number(item.quantity) || 1;
      const delta = selectedQuantity - currentQuantity;

      if (delta !== 0) {
        this.onUpdateQuantity(item.cartId, delta);
      }
    },
    handlePinAction() {
      if (this.pinButtonType === "pin") {
        this.onPin();
      } else {
        this.onUnpin();
      }
    },
    handleCheckout() {
      this.onCheckout();
      // Close drawer if onClose is provided
      if (this.onClose) {
        this.onClose();
      }
    },
    handleBack() {
      // Close the cart so the user can continue ordering
      if (this.onClose) {
        this.onClose();
      }
    }
  }
};
</script>

<style scoped>
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.fadeIn-0-3s {
  animation: fadeIn 0.3s ease-out;
}

.checkout-button:active {
  transform: scale(0.98);
}
</style>
